/**
 * Marketing Terminal — Budget Console + Cross-Channel Analytics
 * Master spec §2.6 + §2.8.
 */

import React, { useState } from 'react';
import { AlertTriangle, BarChart3, Check, DollarSign, Edit3, Pause, TrendingUp, Wallet } from 'lucide-react';
import { QueryClient, QueryClientProvider, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '../../../utils/adminApi';

interface Budget {
  id: string;
  name: string;
  scope: string;
  scope_id: string;
  cap_amount_cents: number;
  spent_cents: number;
  alert_threshold_pct: number;
  alert_threshold_2_pct: number;
  auto_pause_at_cap: boolean;
  status: string;
  period_start: string;
  period_end: string;
  unacked_alerts: number;
  currency: string;
}
interface BudgetAlert {
  id: string;
  budget_id: string;
  budget_name: string;
  scope: string;
  scope_id: string;
  threshold_hit_pct: number;
  spent_cents: number;
  cap_cents: number;
  fired_at: string;
}
interface CrossChannelData {
  by_platform: Array<{ platform: string; impressions: number; engagement: number; clicks: number; conversions: number; spend_cents: number }>;
  by_persona: Array<{ persona_id: string; persona_name: string; engagement: number; conversions: number; spend_cents: number }>;
  window_days: number;
}

const queryClient = new QueryClient({ defaultOptions: { queries: { staleTime: 30_000 } } });

export function BudgetConsole() {
  return (
    <QueryClientProvider client={queryClient}>
      <Inner />
    </QueryClientProvider>
  );
}

function Inner() {
  const [tab, setTab] = useState<'budgets' | 'analytics' | 'alerts'>('budgets');

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: "'League Spartan', sans-serif", color: 'var(--cedar-ink)' }}>
          Budget & Analytics
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Multi-tier budgets with auto-pause + cross-channel analytics with persona attribution.
        </p>
      </div>

      <div className="flex gap-2 border-b border-gray-200">
        {[
          { key: 'budgets', label: 'Budgets', icon: Wallet },
          { key: 'analytics', label: 'Cross-Channel Analytics', icon: BarChart3 },
          { key: 'alerts', label: 'Alerts', icon: AlertTriangle },
        ].map(t => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key as 'budgets' | 'analytics' | 'alerts')}
              className="flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium"
              style={active ? { borderColor: 'var(--cedar-primary)', color: 'var(--cedar-primary)' } : { borderColor: 'transparent', color: 'var(--text-secondary)' }}
            >
              <Icon className="h-4 w-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'budgets' && <BudgetsTab />}
      {tab === 'analytics' && <AnalyticsTab />}
      {tab === 'alerts' && <AlertsTab />}
    </div>
  );
}

function BudgetsTab() {
  const queryClient = useQueryClient();
  const { data } = useQuery<{ data: Budget[] }>({
    queryKey: ['marketing-budgets'],
    queryFn: () => adminFetch('/api/admin/marketing/budgets'),
  });

  const update = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<Budget> }) =>
      adminFetch(`/api/admin/marketing/budgets/${id}`, {
        method: 'PUT',
        body: JSON.stringify(updates),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['marketing-budgets'] }),
  });

  // Group by scope
  const grouped: Record<string, Budget[]> = {};
  data?.data?.forEach(b => {
    grouped[b.scope] = grouped[b.scope] ?? [];
    grouped[b.scope].push(b);
  });

  return (
    <div className="space-y-4">
      {Object.entries(grouped).map(([scope, budgets]) => (
        <div key={scope}>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">{scope} budgets</h3>
          <div className="space-y-2">
            {budgets.map(b => (
              <BudgetRow key={b.id} budget={b} onUpdate={updates => update.mutate({ id: b.id, updates })} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function BudgetRow({ budget, onUpdate }: { budget: Budget; onUpdate: (updates: Partial<Budget>) => void }) {
  const [editing, setEditing] = useState(false);
  const [newCap, setNewCap] = useState((budget.cap_amount_cents / 100).toString());

  const pct = budget.cap_amount_cents > 0 ? (budget.spent_cents / budget.cap_amount_cents) * 100 : 0;
  const barColor = pct >= 100 ? 'var(--status-error)' : pct >= 90 ? 'var(--status-warning)' : pct >= 80 ? 'var(--status-warning-soft)' : 'var(--cedar-primary)';
  const bgColor = pct >= 100 ? 'var(--status-error-bg)' : pct >= 90 ? 'var(--status-warning-bg)' : pct >= 80 ? 'var(--note-bg)' : 'var(--cedar-subtle)';

  return (
    <div className="rounded-lg bg-white p-3" style={{ boxShadow: '0px 1px 3px rgba(0,0,0,0.06)' }}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 text-sm font-bold text-gray-900">
            {budget.name}
            {budget.status !== 'active' && (
              <span className="rounded px-1.5 py-0.5 text-[10px] font-medium uppercase" style={{ backgroundColor: 'var(--status-error-bg)', color: 'var(--status-error-text)' }}>
                {budget.status}
              </span>
            )}
            {budget.unacked_alerts > 0 && (
              <span className="rounded bg-red-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
                {budget.unacked_alerts} alert{budget.unacked_alerts !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          <div className="text-[10px] text-gray-500">
            {budget.scope}/{budget.scope_id} · {budget.period_start} → {budget.period_end}
          </div>
        </div>
        {!editing && (
          <div className="text-right">
            <div className="text-sm font-bold" style={{ color: barColor }}>
              ${(budget.spent_cents / 100).toFixed(2)} / ${(budget.cap_amount_cents / 100).toFixed(2)}
            </div>
            <div className="text-[10px] text-gray-500">{pct.toFixed(0)}% used</div>
          </div>
        )}
      </div>

      {editing ? (
        <div className="mt-2 flex items-center gap-2">
          <span className="text-xs">Cap: $</span>
          <input
            type="number"
            value={newCap}
            onChange={e => setNewCap(e.target.value)}
            className="w-24 rounded border border-gray-200 px-2 py-1 text-xs"
            min={0}
            step={1}
          />
          <button
            onClick={() => {
              onUpdate({ cap_amount_cents: Math.round(parseFloat(newCap) * 100) });
              setEditing(false);
            }}
            className="rounded px-2 py-1 text-xs font-medium text-white"
            style={{ backgroundColor: 'var(--cedar-primary)' }}
          >
            Save
          </button>
          <button onClick={() => setEditing(false)} className="text-xs text-gray-500">Cancel</button>
        </div>
      ) : (
        <>
          <div className="mt-2 h-2 w-full overflow-hidden rounded-full" style={{ backgroundColor: bgColor }}>
            <div className="h-full transition-all" style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: barColor }} />
          </div>
          <div className="mt-2 flex justify-end gap-2 text-[10px]">
            <button onClick={() => setEditing(true)} className="text-gray-600 hover:text-gray-900">
              <Edit3 className="mr-0.5 inline h-2.5 w-2.5" /> Edit cap
            </button>
            {budget.status === 'active' && (
              <button onClick={() => onUpdate({ status: 'paused' })} className="text-amber-600 hover:text-amber-800">
                <Pause className="mr-0.5 inline h-2.5 w-2.5" /> Pause
              </button>
            )}
            {budget.status === 'paused' && (
              <button onClick={() => onUpdate({ status: 'active' })} className="text-green-600 hover:text-green-800">
                <Check className="mr-0.5 inline h-2.5 w-2.5" /> Resume
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function AnalyticsTab() {
  const { data } = useQuery<{ data: CrossChannelData }>({
    queryKey: ['marketing-cross-channel'],
    queryFn: () => adminFetch('/api/admin/marketing/analytics/cross-channel?days=30'),
  });

  const platforms = data?.data?.by_platform ?? [];
  const personas = data?.data?.by_persona ?? [];

  return (
    <div className="space-y-4">
      <div className="rounded-lg bg-white p-4" style={{ boxShadow: '0px 1px 3px rgba(0,0,0,0.06)' }}>
        <h3 className="mb-3 text-sm font-bold text-gray-900">By platform — last 30 days</h3>
        {platforms.length === 0 ? (
          <div className="py-8 text-center text-xs italic text-gray-400">
            <TrendingUp className="mx-auto mb-2 h-8 w-8 text-gray-300" />
            No analytics data yet. Daily sync cron populates this when channels are connected.
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="border-b border-gray-100">
              <tr><th className="pb-2 text-left">Platform</th><th>Impressions</th><th>Engagement</th><th>Clicks</th><th>Conversions</th><th>Spend</th></tr>
            </thead>
            <tbody>
              {platforms.map(p => (
                <tr key={p.platform} className="border-b border-gray-50">
                  <td className="py-2 font-mono">{p.platform}</td>
                  <td className="text-right">{(p.impressions || 0).toLocaleString()}</td>
                  <td className="text-right">{(p.engagement || 0).toLocaleString()}</td>
                  <td className="text-right">{(p.clicks || 0).toLocaleString()}</td>
                  <td className="text-right font-bold">{(p.conversions || 0).toLocaleString()}</td>
                  <td className="text-right">${((p.spend_cents || 0) / 100).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="rounded-lg bg-white p-4" style={{ boxShadow: '0px 1px 3px rgba(0,0,0,0.06)' }}>
        <h3 className="mb-3 text-sm font-bold text-gray-900">By persona — last 30 days</h3>
        {personas.length === 0 ? (
          <div className="py-6 text-center text-xs italic text-gray-400">
            No persona-attributed analytics yet. UTM-tagged content + persona cookie populate this.
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="border-b border-gray-100"><tr><th>Persona</th><th>Engagement</th><th>Conversions</th><th>Spend</th><th>$/conv</th></tr></thead>
            <tbody>
              {personas.map(p => {
                const cpc = p.conversions ? (p.spend_cents || 0) / 100 / p.conversions : 0;
                return (
                  <tr key={p.persona_id} className="border-b border-gray-50">
                    <td className="py-2">{p.persona_name}</td>
                    <td className="text-right">{(p.engagement || 0).toLocaleString()}</td>
                    <td className="text-right font-bold">{(p.conversions || 0).toLocaleString()}</td>
                    <td className="text-right">${((p.spend_cents || 0) / 100).toFixed(2)}</td>
                    <td className="text-right">{cpc > 0 ? `$${cpc.toFixed(2)}` : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function AlertsTab() {
  const queryClient = useQueryClient();
  const { data } = useQuery<{ data: BudgetAlert[] }>({
    queryKey: ['marketing-budget-alerts'],
    queryFn: () => adminFetch('/api/admin/marketing/budgets/alerts'),
  });

  const ack = useMutation({
    mutationFn: (id: string) => adminFetch(`/api/admin/marketing/budgets/alerts/${id}/acknowledge`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['marketing-budget-alerts'] }),
  });

  if (!data?.data?.length) {
    return <div className="rounded-lg bg-white p-8 text-center text-sm text-gray-400" style={{ boxShadow: '0px 1px 3px rgba(0,0,0,0.1)' }}>
      <Check className="mx-auto mb-2 h-8 w-8" style={{ color: 'var(--cedar-primary)' }} />
      All clear — no unacknowledged budget alerts.
    </div>;
  }

  return (
    <div className="space-y-2">
      {data.data.map(a => (
        <div key={a.id} className="flex items-center justify-between rounded-lg bg-amber-50 p-3 text-xs">
          <div>
            <div className="font-bold text-amber-900">
              <AlertTriangle className="mr-1 inline h-3 w-3" />
              {a.budget_name} hit {a.threshold_hit_pct}%
            </div>
            <div className="text-amber-700">
              ${(a.spent_cents / 100).toFixed(2)} / ${(a.cap_cents / 100).toFixed(2)} · {new Date(a.fired_at).toLocaleString()}
            </div>
          </div>
          <button
            onClick={() => ack.mutate(a.id)}
            className="rounded bg-amber-600 px-3 py-1 text-xs font-medium text-white"
          >
            Acknowledge
          </button>
        </div>
      ))}
    </div>
  );
}

export default BudgetConsole;
